<template>
  <div v-if="forceShow || hasAvailableVideos()">
    <!-- Bouton d'aide flottant -->
    <div
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
      @click="openVideo"
      class="help-button-container"
      :title="$t('help.help_title')"
    >
      <!-- Mascotte animée (apparaît au hover) -->
      <transition name="mascot">
        <div v-if="isHovered" class="mascot-container">
          <!-- Bulle de pensée -->
          <div class="thought-bubble">
            <div class="bubble-content">
              <span class="lightbulb-icon">💡</span>
            </div>
            <div class="bubble-tail"></div>
          </div>
          
          <!-- Personnage mascotte -->
          <div class="mascot">
            <div class="mascot-head">
              <div class="mascot-eyes">
                <div class="eye left"></div>
                <div class="eye right"></div>
              </div>
              <div class="mascot-mouth"></div>
            </div>
            <div class="mascot-body"></div>
          </div>
        </div>
      </transition>

      <!-- Bouton principal -->
      <button 
        class="help-button" 
        :class="{ 
          'hovered': isHovered,
          'disabled': forceShow && !hasAvailableVideos()
        }"
        :disabled="forceShow && !hasAvailableVideos()"
      >
        <!-- Icône qui change au hover -->
        <transition name="icon-fade" mode="out-in">
          <i v-if="!isHovered" class="bi bi-question-lg icon"></i>
          <i v-else class="bi bi-play-fill icon play-icon"></i>
        </transition>
      </button>
    </div>

    <!-- Popup de sélection de vidéos -->
    <transition name="popup">
      <div v-if="isVideoListOpen" class="video-popup" @click.stop>
        <!-- Header avec triangle pointant vers la mascotte -->
        <div class="popup-arrow"></div>
        
        <div class="popup-header">
          <h3 class="popup-title">
            <i class="bi bi-collection-play"></i>
            {{ $t('help.select_video_title') || $t('help.select_video_title_fallback') }}
          </h3>
          <button @click="closeVideoList" class="popup-close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        
        <!-- Liste des vidéos -->
        <div class="popup-video-list">
          <div 
            v-for="video in getVideosForCurrentPage()" 
            :key="video.key"
            @click="selectVideo(video.key)"
            class="popup-video-item"
          >
            <div class="popup-video-icon">
              <i class="bi bi-play-circle-fill"></i>
            </div>
            <div class="popup-video-content">
              <h4 class="popup-video-title">{{ getVideoTitleByKey(video.key) }}</h4>
              <p class="popup-video-desc">{{ getVideoDescriptionByKey(video.key) }}</p>
            </div>
            <div class="popup-video-chevron">
              <i class="bi bi-chevron-right"></i>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal vidéo -->
    <transition name="modal">
      <div v-if="isVideoOpen" class="modal-overlay" @click="closeVideo">
        <div class="modal-container" @click.stop>
          <!-- Header -->
          <div class="modal-header">
            <h3 class="modal-title">{{ getVideoTitle() }}</h3>
            <button @click="closeVideo" class="close-button">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          
          <!-- Vidéo -->
          <div class="video-container">
            <iframe
              :src="getVideoUrl()"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              class="video-iframe"
            ></iframe>
          </div>
          
          <!-- Footer -->
          <div class="modal-footer">
            <p class="footer-text">
              <i class="bi bi-info-circle"></i>
              {{ $t('help.footer_support_text') }}
            </p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "HelpButton",
  
  props: {
    currentPage: {
      type: String,
      default: null,
    },
    forceShow: {
      type: Boolean,
      default: false,
    },
  },
  
  data() {
    return {
      isHovered: false,
      isVideoOpen: false,
      isVideoListOpen: false,
      selectedVideoKey: null,
      currentRoute: this.currentPage || this.$route?.name || 'welcome',

      videoUrls: {
        'welcome': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Rapport_dactivite_de_lecole_DQiLe22.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/School_dashboard_5fUxyef.mp4',
        },
        'dashboard': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Rapport_dactivite_de_lecole_DQiLe22.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/School_dashboard_5fUxyef.mp4',
        },
        'create-payment-group': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Creation_dun_groupe_de_paiement_uJWCCmA.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Creation_of_payment_groups_lQDP4tJ.mp4',
        },
        'create-payment-installment': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Creation_dune_tranche_de_paiement_xBEB1LE.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Creation_of_payment_installments_rQNPzL3.mp4',
        },
        'classrooms': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Ajouter_une_salle_de_classe.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Creation_of_classrooms_coABySd.mp4',
        },
        'new-class': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Ajouter_une_salle_de_classe.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Creation_of_classrooms_coABySd.mp4',
        },
        'payment-config': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Appliquer_un_groupe_de_paiement_a_une_classe_BfWnmon.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Setting_the_school_fees_for_every_classroom_3Ks860d.mp4',
        },
        'add-student': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Ajouter_un_eleve_ZGGOqiI.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Adding_students_in_a_classroom_8Nb2bmL.mp4',
        },
        'import-students': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Ajouter_un_eleve_ZGGOqiI.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Adding_students_in_a_classroom_8Nb2bmL.mp4',
        },
        'send-payment-request': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Selection_et_envoi_dune_tranche_de_paiement_HD.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Send_school_fees_payment_requests_to_the_parents_HD.mp4',
        },
        'other-payment': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Configurer_dautres_paiements_PdepQO5.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Configuring_other_payments_31VbC8m.mp4',
        },
        'student-financial-status': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Controler_la_situation_financiere_dun_eleve_NfqQpc2.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Check_the_financial_status_of_a_student_KuJOjuI.mp4',
        },
        'historique-transaction': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Consulter_lhistorique_des_transactions_RDEv22S.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Checking_the_transaction_history_vFIZcxB.mp4',
        },
        'transfer': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Transfert_bancaire_light_HD_.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Bank_transfert_light_HD_.mp4',
        },
        'sync-teachers': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Synchronisation_des_enseignants_HD.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Syncing_teachers_with_Scolarfleet__HD_.mp4',
        },
        'notes': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Consultez_les_notes_des_eleves_V5dNuAK.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Checking_students_grades_submitted_by_teache_xXpOlWh.mp4',
        },
        'message': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Messagerie_enrichie_HD_.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Sending_rich_messages_to_parents__HD_.mp4',
        },
        'users-management': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Gestion_des_Utilisateurs__HD_.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Users_management___HD_.mp4',
        },
        'promotion': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Promotion_des_eleves_HD_.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Promote_students_to_other_classrooms_HD_.mp4',
        },
        'conclusion': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Conclusion_FR_HD_.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Conclusion_En_HD_light_.mp4',
        },
        'create-payment-config': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Creation_dun_groupe_de_paiement_uJWCCmA.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Creation_of_payment_groups_lQDP4tJ.mp4',
        },
        'new-student': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Ajouter_un_eleve_ZGGOqiI.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Adding_students_in_a_classroom_8Nb2bmL.mp4',
        },
        'student-details': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Controler_la_situation_financiere_dun_eleve_NfqQpc2.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Check_the_financial_status_of_a_student_KuJOjuI.mp4',
        },
        'accounts': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Gestion_des_Utilisateurs__HD_.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Users_management___HD_.mp4',
        },
        'update-class': {
          fr: 'https://media3.ikwen.com/tutolight/videos/Promotion_des_eleves_HD_.mp4',
          en: 'https://media3.ikwen.com/tutolight/videos/Promote_students_to_other_classrooms_HD_.mp4',
        },
      },

      multipleVideos: {
        'welcome': [
          {
            key: 'dashboard',
            fr: 'https://media3.ikwen.com/tutolight/videos/Rapport_dactivite_de_lecole_DQiLe22.mp4',
            en: 'https://media3.ikwen.com/tutolight/videos/School_dashboard_5fUxyef.mp4',
          },
          {
            key: 'sync-teachers',
            fr: 'https://media3.ikwen.com/tutolight/videos/Synchronisation_des_enseignants_HD.mp4',
            en: 'https://media3.ikwen.com/tutolight/videos/Syncing_teachers_with_Scolarfleet__HD_.mp4',
          }
        ],
        'class-details': [
          {
            key: 'add-student',
            fr: 'https://media3.ikwen.com/tutolight/videos/Ajouter_un_eleve_ZGGOqiI.mp4',
            en: 'https://media3.ikwen.com/tutolight/videos/Adding_students_in_a_classroom_8Nb2bmL.mp4',
          },
          {
            key: 'send-payment-request',
            fr: 'https://media3.ikwen.com/tutolight/videos/Selection_et_envoi_dune_tranche_de_paiement_HD.mp4',
            en: 'https://media3.ikwen.com/tutolight/videos/Send_school_fees_payment_requests_to_the_parents_HD.mp4',
          },
          {
            key: 'student-financial-status',
            fr: 'https://media3.ikwen.com/tutolight/videos/Controler_la_situation_financiere_dun_eleve_NfqQpc2.mp4',
            en: 'https://media3.ikwen.com/tutolight/videos/Check_the_financial_status_of_a_student_KuJOjuI.mp4',
          },
          {
            key: 'promotion',
            fr: 'https://media3.ikwen.com/tutolight/videos/Promotion_des_eleves_HD_.mp4',
            en: 'https://media3.ikwen.com/tutolight/videos/Promote_students_to_other_classrooms_HD_.mp4',
          }
        ],
        'payment-config': [
          {
            key: 'create-payment-config',
            fr: 'https://media3.ikwen.com/tutolight/videos/Creation_dun_groupe_de_paiement_uJWCCmA.mp4',
            en: 'https://media3.ikwen.com/tutolight/videos/Creation_of_payment_groups_lQDP4tJ.mp4',
          },
          {
            key: 'payment-config',
            fr: 'https://media3.ikwen.com/tutolight/videos/Appliquer_un_groupe_de_paiement_a_une_classe_BfWnmon.mp4',
            en: 'https://media3.ikwen.com/tutolight/videos/Setting_the_school_fees_for_every_classroom_3Ks860d.mp4',
          }
          
        ]
      },

      videoTitles: {
        'welcome': 'Bienvenue - Guide de démarrage',
        'dashboard': 'Rapport d\'activité de l\'école',
        'classrooms': 'Création des salles de classe',
        'new-class': 'Création des salles de classe',
        'class-details': 'Ajouter les élèves dans une classe',
        'payment-config': 'Définir les frais de scolarité pour chaque classe',
        'create-payment-config': 'Création des groupes de paiement',
        'add-student': 'Ajouter les élèves dans une classe',
        'import-students': 'Ajouter les élèves dans une classe',
        'create-payment-group': 'Création des groupes de paiements',
        'create-payment-installment': 'Création des tranches de paiements',
        'other-payment': 'Configurer d\'autres paiements',
        'student-financial-status': 'Contrôler la situation financière d\'un élève',
        'historique-transaction': 'Consulter l\'historique des transactions',
        'transfer': 'Transferts bancaires',
        'sync-teachers': 'Synchronisation des enseignants avec Scolarfleet',
        'notes': 'Consulter les notes des élèves soumises par les enseignants',
        'message': 'Envoyer gratuitement des messages aux parents',
        'users-management': 'Gestion des utilisateurs',
        'promotion': 'Promotion des élèves',
        'accounts': 'Gestion des comptes',
        'new-account': 'Créer un nouveau compte',
        'detail-config': 'Détails de la configuration',
        'update-config': 'Modifier la configuration',
        'new-student': 'Ajouter un nouvel élève',
        'list-payment': 'Liste des paiements',
        'settled-students': 'Élèves à jour',
        'classroom-receipts': 'Reçus de classe',
        'classes-students-payment-settled': 'Paiements réglés',
        'classes-students-payment-overdue': 'Paiements en retard',
        'classes-students-payment-pending': 'Paiements en attente',
        'only-payment-form': 'Formulaire de paiement',
        'sendnote': 'Envoyer une note',
        'recurent-payment-form': 'Paiement récurrent',
        'update-only-payment': 'Modifier le paiement',
        'update-recurent-payment': 'Modifier le paiement récurrent',
        'start-message': 'Nouveau message',
        'pending-students': 'Élèves en attente',
        'overdue-students': 'Élèves en retard',
        'notifications': 'Notifications',
      },

      videoDescriptions: {
        'dashboard': 'Découvrez les fonctionnalités principales du tableau de bord de votre école.',
        'sync-teachers': 'Apprenez à synchroniser les enseignants avec la plateforme Scolarfleet.',
        'add-student': 'Guide pour ajouter les élèves dans une classe.',
        'send-payment-request': 'Comment envoyer les demandes de paiements de scolarité aux parents.',
        'student-financial-status': 'Consultez et contrôlez la situation financière de chaque élève.',
        'promotion': 'Apprenez à promouvoir les élèves d\'une classe à une autre.',
        'create-payment-config': 'Apprenez à créer des groupes de paiement.',
        'payment-config': 'Apprenez à configurer des groupes de paiement.',
      }
    };
  },

  watch: {
    $route(to) {
      this.currentRoute = to.name || 'welcome';
      this.resetModalState();
    },
    currentPage(newVal) {
      if (newVal) {
        this.currentRoute = newVal;
        this.resetModalState();
      }
    }
  },

  methods: {
    openVideo() {
      if (this.hasMultipleVideos(this.currentRoute)) {
        this.isVideoListOpen = true;
        this.selectedVideoKey = null;
      } else {
        this.isVideoOpen = true;
      }
      document.body.style.overflow = 'hidden';
    },
    
    closeVideo() {
      this.isVideoOpen = false;
      document.body.style.overflow = '';
    },

    closeVideoList() {
      this.isVideoListOpen = false;
      this.selectedVideoKey = null;
      document.body.style.overflow = '';
    },

    /**
     * Réinitialise tous les états des modals et nettoie les effets de style
     * Cette méthode est appelée lors du changement de page/route
     */
    resetModalState() {
      this.isVideoOpen = false;
      this.isVideoListOpen = false;
      this.selectedVideoKey = null;
      this.isHovered = false;
      document.body.style.overflow = '';
    },

    selectVideo(videoKey) {
      this.selectedVideoKey = videoKey;
      this.isVideoListOpen = false;
      this.isVideoOpen = true;
    },

    hasMultipleVideos(pageKey) {
      return Object.prototype.hasOwnProperty.call(this.multipleVideos, pageKey);
    },

    getVideosForCurrentPage() {
      return this.multipleVideos[this.currentRoute] || [];
    },

    getVideoUrl() {
      let urls;
      
      if (this.selectedVideoKey) {
        const videoData = this.getVideosForCurrentPage().find(v => v.key === this.selectedVideoKey);
        if (videoData) {
          urls = { fr: videoData.fr, en: videoData.en };
        }
      } else {
        // Mapper la route /synchronisation-teacher à la vidéo sync-teachers
        const routeToVideoKey = this.getVideoKeyForRoute();
        urls = this.videoUrls[routeToVideoKey] || this.videoUrls[this.currentRoute] || this.videoUrls['welcome'];
      }
      
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : (this.$t && typeof this.$t === 'function' && this.$t('lang')) || 'fr';
      return (urls && (urls[locale] || urls['fr'])) || '';
    },
    
    getVideoTitle() {
      let titleKey = this.currentRoute;
      
      if (this.selectedVideoKey) {
        titleKey = this.selectedVideoKey;
      } else {
        // Utiliser le mapping route -> vidéo aussi pour le titre
        const routeToVideoKey = this.getVideoKeyForRoute();
        if (routeToVideoKey) {
          titleKey = routeToVideoKey;
        }
      }
      
      const key = `help.video_title.${titleKey}`;
      const translated = this.$t && typeof this.$t === 'function' ? this.$t(key) : null;
      if (translated && translated !== key) return translated;
      return this.videoTitles[titleKey] || this.$t('help.video_title.default') || this.$t('help.fallback_title');
    },

    getVideoTitleByKey(videoKey) {
      const key = `help.video_title.${videoKey}`;
      const translated = this.$t && typeof this.$t === 'function' ? this.$t(key) : null;
      if (translated && translated !== key) return translated;
      return this.videoTitles[videoKey] || this.$t('help.fallback_title');
    },

    getVideoDescriptionByKey(videoKey) {
      const key = `help.video_desc.${videoKey}`;
      const translated = this.$t && typeof this.$t === 'function' ? this.$t(key) : null;
      if (translated && translated !== key) return translated;
      return this.videoDescriptions[videoKey] || this.$t('help.video_desc.default');
    },

    /**
     * Mappe le nom de route à la clé vidéo correspondante
     * @returns {string} - La clé vidéo对应 à la route courante
     */
    getVideoKeyForRoute() {
      // Mapping des routes vers les clés vidéos
      const routeMappings = {
        '/synchronisation-teacher': 'sync-teachers',
        'synchronisation-teacher': 'sync-teachers',
        '/synchronisation-teacher-sync-requests': 'sync-teachers',
        'synchronisation-teacher-sync-requests': 'sync-teachers'
      };

      // Retourner la clé vidéo correspondante ou null si pas de correspondance
      return routeMappings[this.currentRoute] || null;
    },

    /**
     * Vérifie si des vidéos sont disponibles pour la page courante
     * @returns {boolean} - true si des vidéos sont disponibles, false sinon
     */
    hasAvailableVideos() {
      // Vérifier si la page a des vidéos multiples
      if (this.hasMultipleVideos(this.currentRoute)) {
        return true;
      }
      
      // Vérifier si la page a une vidéo unique
      const routeToVideoKey = this.getVideoKeyForRoute();
      if (routeToVideoKey && this.videoUrls[routeToVideoKey]) {
        return true;
      }
      
      // Vérifier si la route courante a une vidéo directe dans videoUrls
      if (this.videoUrls[this.currentRoute]) {
        return true;
      }
      
      return false;
    }
  },
  
  beforeUnmount() {
    // Nettoyage complet de tous les états lors de la destruction du composant
    this.resetModalState();
  }
};
</script>

<style lang="scss" scoped>
/* Variables CSS pour le système de design moderne */
:root {
  --primary-color: #6366f1;
  --secondary-color: #8b5cf6;
  --accent-color: #f59e0b;
  --danger-color: #ef4444;
  --text-dark: #1f2937;
  --text-light: #6b7280;
  --text-white: #ffffff;
  --success-color: #10b981;
  --shadow-primary: rgba(99, 102, 241, 0.3);
  --shadow-accent: rgba(245, 158, 11, 0.4);
  --gradient-primary: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  --gradient-accent: linear-gradient(135deg, var(--accent-color) 0%, var(--danger-color) 100%);
  --gradient-header: linear-gradient(135deg, #000000 0%, #161e2e 100%);
  --border-radius: 12px;
  --border-radius-lg: 20px;
  --backdrop-blur: blur(8px);
}

$primary-color: var(--primary-color);
$secondary-color: var(--secondary-color);
$text-dark: var(--text-dark);
$text-light: var(--text-light);
$white: var(--text-white);
$shadow: var(--shadow-primary);

.help-button-container {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 9999;
  cursor: pointer;
}

.help-button {
  position: relative;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: scale(1.1) translateY(-2px);
    box-shadow: 0 12px 32px rgba(99, 102, 241, 0.3);
  }
  
  &.hovered {
    background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
  }
  
  .icon {
    font-size: 16px;
    color: #ffffff;
    transition: all 0.3s ease;
  }
  
  .play-icon {
    font-size: 18px;
    margin-left: 2px;
  }
  
  &.disabled {
    background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: 0 2px 8px rgba(156, 163, 175, 0.3);
    
    &:hover {
      transform: none;
      box-shadow: 0 2px 8px rgba(156, 163, 175, 0.3);
    }
    
    .icon {
      color: #f3f4f6;
    }
  }
}

.mascot-container {
  position: absolute;
  bottom: 80px;
  right: -20px;
  animation: mascotAppear 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes mascotAppear {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.thought-bubble {
  position: absolute;
  top: -40px;
  right: 10px;
  animation: float 2s ease-in-out infinite;
  
  .bubble-content {
    background: #ffffff;
    border-radius: 20px;
    padding: 6px 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    position: relative;
    
    .lightbulb-icon {
      font-size: 16px;
      font-weight: bold;
      color: #6366f1;
      animation: questionBounce 1s ease-in-out infinite;
      display: inline-block;
    }
  }
  
  .bubble-tail {
    position: absolute;
    bottom: -8px;
    right: 15px;
    width: 10px;
    height: 10px;
    background: #ffffff;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    
    &::after {
      content: '';
      position: absolute;
      bottom: -7px;
      right: 2px;
      width: 5px;
      height: 5px;
      background: #ffffff;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes questionBounce {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

.mascot {
  width: 40px;
  height: 54px;
  position: relative;
  
  .mascot-head {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    border-radius: 50%;
    position: relative;
    margin: 0 auto;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
    animation: headBob 1.5s ease-in-out infinite;
    
    .mascot-eyes {
      display: flex;
      gap: 8px;
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      
      .eye {
        width: 5px;
        height: 5px;
        background: #1f2937;
        border-radius: 50%;
        animation: blink 3s infinite;
      }
    }
    
    .mascot-mouth {
      position: absolute;
      bottom: 6px;
      left: 50%;
      transform: translateX(-50%);
      width: 12px;
      height: 6px;
      border: 1.5px solid #1f2937;
      border-top: none;
      border-radius: 0 0 20px 20px;
    }
  }
  
  .mascot-body {
    width: 18px;
    height: 20px;
    background: linear-gradient(135deg, rgb(129, 36, 251) 0%, #f59e0b 100%);
    border-radius: 15px 15px 20px 20px;
    margin: -3px auto 0;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  }
}

@keyframes headBob {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes blink {
  0%, 48%, 52%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(0.1);
  }
}

.mascot-enter-active, .mascot-leave-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.mascot-enter-from, .mascot-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

.icon-fade-enter-active, .icon-fade-leave-active {
  transition: all 0.2s ease;
}

.icon-fade-enter-from, .icon-fade-leave-to {
  opacity: 0;
  transform: scale(0.8) rotate(90deg);
}

.video-popup {
  position: fixed;
  bottom: 110px;
  right: 2rem;
  width: 420px;
  max-height: 500px;
  background: $white;
  border-radius: var(--border-radius-lg);
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 9998;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: popupSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  backdrop-filter: var(--backdrop-blur);
  -webkit-backdrop-filter: var(--backdrop-blur);
}

@keyframes popupSlideIn {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.popup-arrow {
  position: absolute;
  bottom: -10px;
  right: 35px;
  width: 20px;
  height: 20px;
  background: $white;
  transform: rotate(45deg);
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.1);
}

// Modal
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
}

.modal-container {
  background: $white;
  border-radius: 24px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  color: $text-dark;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #000000 0%, #161e2e 100%);
  
  .modal-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: $text-dark;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &::before {
      content: '🎬';
      font-size: 1.75rem;
    }
  }
  
  .close-button {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    border: none;
    background: rgba(255, 255, 255, 0);
    color: $white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(0, 0, 0, 0.1);
      transform: scale(1.1);
    }
    
    i {
      font-size: 20px;
      color: $white;
    }
  }
}

.video-container {
  flex: 1;
  position: relative;
  background: #000;
  min-height: 400px;
  
  .video-iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.modal-footer {
  padding: 1rem 2rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  
  .footer-text {
    margin: 0;
    color: $text-light;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    i {
      color: $primary-color;
    }
  }
}

.modal-enter-active, .modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
  
  .modal-container {
    transform: scale(0.9) translateY(20px);
  }
}

// Popup video list - Style moderne pour la sélection de vidéos
.popup-video-list {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%);
}

.popup-video-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.75rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  
  &:hover {
    background: var(--gradient-primary);
    border-color: var(--primary-color);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 
      0 8px 25px var(--shadow-primary),
      0 0 0 1px rgba(99, 102, 241, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
}

.popup-video-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: var(--gradient-primary);
  border-radius: var(--border-radius);
  margin-right: 1rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px var(--shadow-primary);
  transition: all 0.3s ease;
  
  .popup-video-item:hover & {
    background: linear-gradient(135deg, var(--text-white) 0%, rgba(255, 255, 255, 0.9) 100%);
    transform: scale(1.1);
    
    i {
      color: var(--primary-color);
    }
  }
  
  i {
    font-size: 24px;
    color: $white;
    transition: all 0.3s ease;
  }
}

.popup-video-content {
  flex: 1;
  min-width: 0;
  
  .popup-video-title {
    font-size: 1rem;
    font-weight: 600;
    color: $text-dark;
    margin: 0 0 0.25rem 0;
    line-height: 1.4;
    transition: all 0.3s ease;
  }
  
  .popup-video-desc {
    font-size: 0.875rem;
    color: $text-light;
    margin: 0;
    line-height: 1.4;
    transition: all 0.3s ease;
  }
}

.popup-video-chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: 1rem;
  flex-shrink: 0;
  transition: all 0.3s ease;
  
  i {
    font-size: 18px;
    color: $text-light;
    transition: all 0.3s ease;
  }
}

.popup-video-item:hover {
  .popup-video-title,
  .popup-video-desc {
    color: $white;
  }
  
  .popup-video-chevron i {
    color: $white;
    transform: translateX(4px);
  }
}

// Header du popup avec style moderne
.popup-header {
  background: var(--gradient-header);
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  .popup-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: $white;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    i {
      font-size: 1.5rem;
      color: var(--accent-color);
    }
  }
  
  .popup-close {
    width: 36px;
    height: 36px;
    border-radius: var(--border-radius);
    border: none;
    background: rgba(255, 255, 255, 0.1);
    color: $white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: scale(1.1);
    }
    
    i {
      font-size: 18px;
    }
  }
}

// Animation améliorée pour l'entrée du popup
@keyframes popupSlideIn {
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

// Scrollbar personnalisée pour le popup
.popup-video-list::-webkit-scrollbar {
  width: 6px;
}

.popup-video-list::-webkit-scrollbar-track {
  background: rgba(99, 102, 241, 0.1);
  border-radius: 3px;
}

.popup-video-list::-webkit-scrollbar-thumb {
  background: var(--gradient-primary);
  border-radius: 3px;
}

.popup-video-list::-webkit-scrollbar-thumb:hover {
  background: var(--secondary-color);
}

// Responsive design amélioré
@media (max-width: 768px) {
  .help-button-container {
    bottom: 1.5rem;
    right: 1.5rem;
  }
  
  .help-button {
    width: 56px;
    height: 56px;
  }
  
  .modal-container {
    border-radius: var(--border-radius);
    max-height: 95vh;
    margin: 0.5rem;
  }
  
  .video-container {
    min-height: 250px;
  }

  .video-popup {
    width: calc(100vw - 3rem);
    bottom: 100px;
    right: 1.5rem;
    left: 1.5rem;
    max-height: 60vh;
  }

  .popup-video-list {
    padding: 0.75rem;
  }

  .popup-video-item {
    padding: 0.75rem;
    
    .popup-video-icon {
      width: 40px;
      height: 40px;
      
      i {
        font-size: 20px;
      }
    }
    
    .popup-video-content {
      .popup-video-title {
        font-size: 0.9rem;
      }
      
      .popup-video-desc {
        font-size: 0.8rem;
      }
    }
  }

  .popup-header {
    padding: 1rem 1.25rem;
    
    .popup-title {
      font-size: 1.1rem;
    }
  }
}

// Animations d'entrée et de sortie améliorées
.popup-enter-active,
.popup-leave-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.popup-enter-from,
.popup-leave-to {
  opacity: 0;
  transform: translateY(30px) scale(0.9);
}

.popup-enter-to,
.popup-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

// Effet de surbrillance pour les éléments interactifs
.popup-video-item:focus {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

// Animation de pulsation pour le bouton d'aide
@keyframes helpButtonPulse {
  0% {
    box-shadow: 0 8px 24px var(--shadow-primary);
  }
  50% {
    box-shadow: 0 8px 24px var(--shadow-primary), 0 0 0 0 rgba(99, 102, 241, 0.7);
  }
  100% {
    box-shadow: 0 8px 24px var(--shadow-primary);
  }
}

.help-button {
  animation: helpButtonPulse 2s infinite;
}

// Styles pour le mode sombre (si applicable)
@media (prefers-color-scheme: dark) {
  .video-popup {
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.98) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .popup-video-list {
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.98) 100%);
  }
  
  .popup-video-item {
    background: linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.8) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
}

// Optimisation des performances pour les animations
.popup-video-item,
.popup-video-icon,
.popup-video-chevron {
  will-change: transform;
}

// Accessibilité - réduction des animations pour les utilisateurs sensibles
@media (prefers-reduced-motion: reduce) {
  .popup-video-item,
  .popup-video-icon,
  .help-button {
    transition: none;
    animation: none;
  }
  
  .popup-enter-active,
  .popup-leave-active {
    transition: none;
  }
}
</style>